// Design-system reminder: every title uses a typography.h* preset, OR `font-heading font-bold`.
import React, { useCallback, useContext, useRef, useState } from "react";
import {
  borders,
  surfaces,
  textColors,
  typography,
} from "../design-system/tokens";
import PartnerTermsContent, {
  PARTNER_TERMS_LAST_UPDATED,
} from "../app/legal/PartnerTermsContent";
import AffiliatePrimaryButton from "./components/AffiliatePrimaryButton";
import { AffiliateAuthContext } from "./context/AffiliateAuthContext";
import { acceptPartnerTerms } from "./utils/affiliateApi";

/**
 * Blocks the partner portal until the current Partner Program Terms are
 * accepted. The full text is shown inline so the partner reads exactly what
 * they agree to; the agree checkbox unlocks once they have reached the end of
 * the text. Mirrors AffiliateTermsGate but submits partner-terms acceptance.
 */
const PartnerTermsGate = ({ requiredVersion }) => {
  const { refreshAffiliateAuth } = useContext(AffiliateAuthContext) || {};
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  const handleScroll = useCallback((event) => {
    const el = event.currentTarget;
    const scrollable = el.scrollHeight > el.clientHeight + 8;
    if (!scrollable || el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setHasReachedEnd(true);
    }
  }, []);

  // Short viewports (and environments that do not lay out scroll boxes) have
  // nothing to scroll, so the text is already fully reachable.
  const markReadableWithoutScroll = useCallback((el) => {
    scrollRef.current = el;
    if (el && el.scrollHeight <= el.clientHeight + 8) setHasReachedEnd(true);
  }, []);

  const submit = async () => {
    if (!agreed || submitting) return;
    setError("");
    setSubmitting(true);
    try {
      await acceptPartnerTerms({ version: requiredVersion });
      if (typeof refreshAffiliateAuth === "function") await refreshAffiliateAuth();
    } catch (e) {
      setError(e?.message || "Could not record your acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-6 sm:p-8 shadow-sm`}>
        <h1 className={`${typography.h2} ${textColors.primary}`}>Partner Program Terms of Use</h1>
        <p className={`${typography.body} ${textColors.secondary} mt-2`}>
          Before you can use the partner portal, please read and accept these terms. They cover how
          linked users are tracked, how coupon packs are priced and purchased, and what your coupons
          grant.
        </p>
        <p className={`${typography.caption} ${textColors.muted} mt-1`}>
          Version {requiredVersion || ""} — last updated {PARTNER_TERMS_LAST_UPDATED}.{" "}
          <a href="/partner-terms" target="_blank" rel="noopener noreferrer" className="underline">
            Open in a new tab
          </a>
        </p>

        <div
          ref={markReadableWithoutScroll}
          onScroll={handleScroll}
          data-testid="partner-terms-scroll"
          tabIndex={0}
          role="region"
          aria-label="Partner Program Terms of Use"
          className={`mt-6 max-h-[55vh] overflow-y-auto rounded-xl border ${borders.glass} ${surfaces.primary} p-5 sm:p-6`}
        >
          <PartnerTermsContent />
        </div>

        {!hasReachedEnd ? (
          <p className={`${typography.caption} ${textColors.muted} mt-3`}>
            Scroll to the end of the terms to continue.
          </p>
        ) : null}

        <label className="mt-5 flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            disabled={!hasReachedEnd}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-dark-600 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
          />
          <span className={`${typography.bodySmall} ${textColors.secondary}`}>
            I have read and agree to the Partner Program Terms of Use.
          </span>
        </label>

        {error ? (
          <p className={`${typography.bodySmall} text-red-600 dark:text-red-400 mt-3`} role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-5">
          <AffiliatePrimaryButton
            type="button"
            onClick={submit}
            disabled={!agreed || submitting}
            loading={submitting}
          >
            {submitting ? "Saving…" : "Agree and continue"}
          </AffiliatePrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default PartnerTermsGate;
